// @file Surface posts store
import { getVuexStore } from '@@/bits/pinia'
import { getPostsByGroupByProperty, Group } from '@@/bits/surface_posts_grouping_helper'
import { unboundedWatch, unboundedWatchEffect } from '@@/bits/vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { usePostSorter } from '@@/vuecomposables/surface_post_sorter'
import { isEmpty } from '@@/vuexstore/helpers/post'
import { GroupByTypes } from '@padlet/arvo'
import { postFilterResults } from 'native_bridge/actions'
import postMessage from 'native_bridge/post_message'
import { defineStore, storeToRefs } from 'pinia'
import { useSurfaceCurrentUserStore } from 'pinia/surface_current_user'
import { filterPosts, useSurfaceFilterStore } from 'pinia/surface_filter'
import { useSurfacePermissionsStore } from 'pinia/surface_permissions'
import { useSurfacePostSearchStore } from 'pinia/surface_post_search'
import { useSurfaceWishArrangementStore } from 'pinia/surface_wish_arrangement'
import type { Cid, Post, PostAttributes } from 'types'
import { computed, ComputedRef, ref } from 'vue'
import type { RootState } from 'vuexstore/surface/types'

export const useSurfacePostsStore = defineStore('surfacePosts', () => {
  const surfaceVuexStore = getVuexStore<RootState>()

  const { isSectioned } = storeToRefs(useSurfaceStore())
  const { currentUser } = storeToRefs(useSurfaceCurrentUserStore())
  const { sortedGroups, wishGroupBy, isSectionedAndNonmanualSorted, isNotSectionedAndNonmanualSorted } = storeToRefs(
    useSurfaceWishArrangementStore(),
  )
  const { hasSelectedFilters } = storeToRefs(useSurfaceFilterStore())
  const { canIModerate, canIWrite } = storeToRefs(useSurfacePermissionsStore())

  const { sortPosts } = usePostSorter()

  /*
   * DRAGGING POSTS
   */

  const isAPostDragging = ref(false)
  const isAPostDraggingAndNonmanualSorted = computed(() => {
    return isAPostDragging.value && isSectionedAndNonmanualSorted.value
  })
  const shouldAlwaysDisableDrag = computed(
    () => searchTerm.value !== '' || hasSelectedFilters.value || isNotSectionedAndNonmanualSorted.value,
  )
  const postCidBeingDragged = ref<Cid | undefined>(undefined)
  const postBeingDragged = computed(() => {
    if (postCidBeingDragged.value == null) return undefined
    return postEntitiesByCid.value[postCidBeingDragged.value]
  })

  // have this permission here instead of surface_permissions to avoid circular dependency between surface_posts and surface_permissions
  // so we avoid having to import postBeingDragged to surface_permissions
  const canIDragPosts = computed(() => {
    // drag always not allowed if these conditions are true
    if (shouldAlwaysDisableDrag.value) {
      return false
    }

    const wasWrittenByCurrentUser =
      currentUser.value?.id != null && postBeingDragged?.value?.author_id === currentUser.value?.id
    // check if user has permission to drag the post
    if (canIModerate.value || (wasWrittenByCurrentUser && canIWrite.value)) {
      // if user has permission to drag post we only allow dragging posts when they're grouped by certain properties
      return [GroupByTypes.None, GroupByTypes.Section].includes(wishGroupBy.value)
    }

    return false
  })

  const hoveredGroupIdWhenDraggingPost = ref<Group['id'] | undefined>(undefined)

  function canShowDraggingOverlay(groupId: Group['id']): boolean {
    return isAPostDraggingAndNonmanualSorted.value && hoveredGroupIdWhenDraggingPost.value === groupId
  }

  // Sync isAPostDragging with Vuex
  void unboundedWatch(isAPostDragging, (isDragging) => {
    void surfaceVuexStore?.dispatch('changeDragging', { isAPostDragging: isDragging })
  })

  /*
   * POST COUNT GETTERS
   */

  const currentPostsCount = computed(() => currentSortedPosts.value.length)

  /*
   * POST COLLECTION GETTERS
   */

  const allPosts = computed<Post[]>(() => surfaceVuexStore?.getters['post/savedPostsArray'] ?? [])
  const allSortedPosts = computed<Post[]>(() => sortPosts(allPosts.value))
  const sortedPostCids = computed(() => allSortedPosts.value.map((p) => p.cid) ?? [])
  const currentSortedPosts = computed(() => sortPosts(currentPosts.value))

  /*
   * POST MAP GETTERS
   */

  const allSortedPostsByGroup: ComputedRef<{
    [x: string]: Post[] | undefined
    [x: number]: Post[] | undefined
  }> = computed(() => {
    const sortedGroupIds = sortedGroups.value.map((group) => group.id)
    return getPostsByGroupByProperty(allSortedPosts.value, sortedGroupIds, wishGroupBy.value)
  })
  // Map of group ID to posts. Order of groups not guaranteed.
  const postsByGroup: ComputedRef<{
    [x: string]: Post[] | undefined
    [x: number]: Post[] | undefined
  }> = computed(() => {
    const sortedGroupIds = sortedGroups.value.map((group) => group.id)
    return getPostsByGroupByProperty(currentSortedPosts.value, sortedGroupIds, wishGroupBy.value)
  })
  // Posts by groups, but in order by which groups are sorted.
  const postsBySortedGroups: ComputedRef<Post[][]> = computed(() => {
    return sortedGroups.value.map((group) => postsByGroup.value[group.id]).filter((group) => group != null) as Post[][]
  })

  const postsBySectionId = computed<{ [sectionId: number]: Post[] }>(
    () => surfaceVuexStore?.getters['post/postsBySectionId'] ?? {},
  )
  const sortedPostCidsBySectionId = computed<Record<string, Cid[]>>(() => {
    if (isSectioned.value) {
      const sortedPostCidsBySectionId = {}
      sortedPostCids.value.forEach((postCid) => {
        const post = postEntitiesByCid.value[postCid]
        if (post.wall_section_id == null || isEmpty(post)) return
        if (sortedPostCidsBySectionId[post.wall_section_id] == null) {
          sortedPostCidsBySectionId[post.wall_section_id] = []
        }
        sortedPostCidsBySectionId[post.wall_section_id].push(postCid)
      })
      return sortedPostCidsBySectionId
    }
    return {}
  })

  /*
   * OTHER POST GETTERS
   */

  const postEntitiesByCid = computed<{ [postCid: string]: Post }>(
    () => surfaceVuexStore?.getters['post/postEntitiesByCid'] ?? {},
  )
  const postEntities = computed<Post[]>(() => Object.values(postEntitiesByCid.value))
  const postCids = computed<string[]>(() => surfaceVuexStore?.getters['post/postCids'] ?? [])
  const newestPostCid = computed(() => surfaceVuexStore?.getters['post/newestPostCid'] ?? [])

  const hasFetchedPosts = computed<boolean>(() => surfaceVuexStore?.getters['post/isInitialPaginatedDataLoadDone'])

  /*
   * POST SEARCH
   */

  const surfacePostSearch = useSurfacePostSearchStore()
  const { filterPostsBySearchTerm } = surfacePostSearch
  const { searchTerm } = storeToRefs(surfacePostSearch)
  const { filters } = storeToRefs(useSurfaceFilterStore())

  const currentPosts = ref<Post[]>(allPosts.value)

  void unboundedWatchEffect(() => {
    const filteredPosts = filterPosts(allPosts.value, filters.value)
    void filterPostsBySearchTerm(filteredPosts, searchTerm.value).then((results) => {
      currentPosts.value = results
      postMessage(postFilterResults(results.length))
    })
  })
  /*
   * POST ACTIONS
   */

  function startNewPost({
    attributes,
    wishOrder,
  }: {
    attributes?: PostAttributes
    wishOrder?: { previousCid: Cid; nextCid: Cid }
  }): void {
    void surfaceVuexStore?.dispatch('post/startNewPost', { attributes, wishOrder })
  }

  function sortPost(payload: {
    postCid: Cid
    previousCid?: Cid | null
    nextCid?: Cid | null
    sortIndex?: number | null
    sectionId?: number | null
    gapSize?: number | null
  }): void {
    void surfaceVuexStore?.dispatch('post/sortedPost', payload)
  }

  function shouldDisplayPost(postCid: Cid): boolean {
    // For future reference, this function used to look like:
    // return currentSortedPostsCids.value.includes(postCid)
    //
    // But we ran into a bug where there are duplicate posts
    // in the padlet if `wishSortBy` is `shuffle`. This
    // `shouldDisplayPost` function called in the `v-show`
    // directive somehow causes a second render that passes
    // duplicate posts to the `SurfacePost` component.
    // We still don't know why, but it's better to avoid
    // referencing "sorted" post getters in this function.

    return currentPosts.value.some((p) => p.cid === postCid)
  }

  return {
    // DRAGGING POSTS
    isAPostDragging,
    isAPostDraggingAndNonmanualSorted,
    shouldAlwaysDisableDrag,
    postCidBeingDragged,
    postBeingDragged,
    canIDragPosts,
    hoveredGroupIdWhenDraggingPost,
    canShowDraggingOverlay,

    // POST COUNT GETTERS
    currentPostsCount,

    // POST COLLECTION GETTERS
    allSortedPosts, // all posts, sorted
    sortedPostCids,
    currentPosts: computed(() => currentPosts.value), // filtered posts, not sorted. exported as computed so it can't be mutated.
    currentSortedPosts, // filtered posts, sorted

    // POST MAP GETTERS
    allSortedPostsByGroup, // map of all posts by group, sorted
    postsByGroup, // map of filtered posts by group, sorted
    postsBySortedGroups, // 2D array of filtered posts, sorted
    postsBySectionId, // map of posts by section ID
    sortedPostCidsBySectionId, // map of post CIDs by section ID, sorted

    // OTHER POST GETTERS
    postEntitiesByCid,
    postEntities,
    postCids,
    newestPostCid,
    hasFetchedPosts,

    // POST ACTIONS
    startNewPost,
    sortPost,
    shouldDisplayPost,
  }
})
